import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, fileUrl, timeAgoCa } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useUnreadMessages } from '../lib/unreadMessages';
import { useT } from '../i18n';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { ComicBubble } from '../components/ComicBubble';
import { Users, Send, Image as ImageIcon, Shield, ShieldOff, Film, X, Banknote } from 'lucide-react';
import { toast } from 'sonner';

export default function FriendsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const t = useT();
    const { total: unread, refresh: refreshUnread } = useUnreadMessages();
    const [friends, setFriends] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [moneyBusy, setMoneyBusy] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [showMoneyForm, setShowMoneyForm] = useState(false);
    const [moneyAmount, setMoneyAmount] = useState('');
    const [moneyPassword, setMoneyPassword] = useState('');
    const [moneyPassword2, setMoneyPassword2] = useState('');
    const [pinChallenge, setPinChallenge] = useState(null);
    const [pinDigits, setPinDigits] = useState({});
    const [showBlocked, setShowBlocked] = useState(false);
    const [blocked, setBlocked] = useState([]);
    const fileInputRef = useRef(null);
    const scrollRef = useRef(null);
    const pollRef = useRef(null);

    const loadFriends = useCallback(async () => {
        try {
            const r = await api.get('/friends');
            setFriends(r.data.items || []);
        } catch {}
    }, []);

    useEffect(() => {
        if (!user) { navigate('/entrar'); return; }
        loadFriends();
        api.get('/users/me/wallet').then((r) => setWalletBalance(r.data.balance || 0)).catch(() => {});
    }, [user, navigate, loadFriends]);

    const loadMessages = useCallback(async (friendId) => {
        try {
            const r = await api.get(`/friends/${friendId}/messages`, { params: { limit: 60 } });
            setMessages(r.data.items || []);
            loadFriends();
            // Opening a conversation marks messages as read, so refresh the
            // global counter so the nav bubble updates right away.
            refreshUnread();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut carregar la conversa');
        }
    }, [loadFriends, refreshUnread]);

    useEffect(() => {
        if (!active) return;
        loadMessages(active.user.user_id);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => loadMessages(active.user.user_id), 3000);
        return () => clearInterval(pollRef.current);
    }, [active, loadMessages]);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const openBlocked = async () => {
        try {
            const r = await api.get('/friends/blocked');
            setBlocked(r.data.items || []);
            setShowBlocked(true);
        } catch {}
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!active || sending) return;
        if (!text.trim()) return;
        setSending(true);
        try {
            const form = new FormData();
            form.append('content', text.trim());
            const r = await api.post(`/friends/${active.user.user_id}/messages`, form);
            setMessages((cur) => [...cur, r.data]);
            setText('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut enviar el missatge');
        } finally {
            setSending(false);
        }
    };

    const sendImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !active) return;
        setSending(true);
        try {
            const form = new FormData();
            form.append('image', file);
            const r = await api.post(`/friends/${active.user.user_id}/messages`, form);
            setMessages((cur) => [...cur, r.data]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut enviar la foto');
        } finally {
            setSending(false);
        }
    };

    const openMoneyForm = async () => {
        if (!active) return;
        setShowMoneyForm((cur) => !cur);
        setPinChallenge(null);
        setPinDigits({});
        try {
            const r = await api.get('/users/me/wallet/money-pin/challenge');
            setPinChallenge(r.data);
        } catch {
            setPinChallenge({ has_pin: false });
        }
    };

    const confirmSendMoney = async () => {
        if (!active || moneyBusy) return;
        const amount = parseFloat(String(moneyAmount).replace(',', '.'));
        if (!amount || amount <= 0) { toast.error('Quantitat no vàlida'); return; }
        if (amount > walletBalance) { toast.error('Saldo insuficient'); return; }
        if (!moneyPassword || moneyPassword !== moneyPassword2) { toast.error('Les dues contrasenyes han de coincidir'); return; }
        setMoneyBusy(true);
        try {
            const r = await api.post(`/friends/${active.user.user_id}/send-money`, { amount, password: moneyPassword, pin_digits: pinDigits });
            setWalletBalance(r.data.balance || 0);
            toast.success(`Has enviat ${amount.toFixed(2)} EUR a ${active.user.name}`);
            setShowMoneyForm(false);
            setMoneyAmount('');
            setMoneyPassword('');
            setMoneyPassword2('');
            setPinDigits({});
            loadMessages(active.user.user_id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut enviar els diners');
            try {
                const c = await api.get('/users/me/wallet/money-pin/challenge');
                setPinChallenge(c.data);
                setPinDigits({});
            } catch {}
        } finally {
            setMoneyBusy(false);
        }
    };

    const blockFriend = async (friendId) => {
        if (!window.confirm(t('friends.block_confirm'))) return;
        try {
            await api.post(`/friends/${friendId}/block`);
            toast.success('Usuari bloquejat');
            if (active?.user.user_id === friendId) setActive(null);
            loadFriends();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut bloquejar');
        }
    };

    const unblockFriend = async (friendId) => {
        try {
            await api.delete(`/friends/${friendId}/block`);
            setBlocked((cur) => cur.filter((u) => u.user_id !== friendId));
            toast.success('Usuari desbloquejat');
            loadFriends();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut desbloquejar');
        }
    };

    const acceptRequest = async (friendId) => {
        try {
            await api.post(`/friends/${friendId}/accept-request`);
            toast.success(t('friends.accepted'));
            loadFriends();
            refreshUnread();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut acceptar la sol\u00b7licitud');
        }
    };

    const ignoreRequest = async (friendId) => {
        try {
            await api.post(`/friends/${friendId}/ignore-request`);
            loadFriends();
            refreshUnread();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut ignorar la sol\u00b7licitud');
        }
    };

    if (!user) return null;

    const requests = friends.filter((f) => f.is_request);
    const chatFriends = friends.filter((f) => !f.is_request);

    return (
        <main className="mx-auto max-w-[1200px] px-3 sm:px-5 lg:px-8 py-6 pb-24 md:pb-12" data-testid="friends-page">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Users className="h-6 w-6 text-[color:var(--alb-sun)]" /> {t('friends.title')}</h1>
                    <p className="mt-1 text-sm text-[color:var(--alb-text-muted)]">{t('friends.subtitle')}</p>
                    {unread > 0 && (
                        <div className="mt-3 flex items-center gap-3" data-testid="friends-unread-bubble">
                            <ComicBubble count={unread} size="md" tail="up" />
                            <span className="text-sm text-[color:var(--alb-text-muted)]">{t('friends.unread_count_label', { n: unread })}</span>
                        </div>
                    )}
                </div>
                <Button variant="outline" onClick={openBlocked} data-testid="friends-blocked-button"><ShieldOff className="h-4 w-4 mr-1" /> {t('friends.blocked_users_title')}</Button>
            </div>

            {showBlocked && (
                <Card className="p-4 mb-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="friends-blocked-panel">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-semibold">{t('friends.blocked_users_title')}</h2>
                        <Button variant="ghost" size="icon" onClick={() => setShowBlocked(false)}><X className="h-4 w-4" /></Button>
                    </div>
                    {blocked.length === 0 ? (
                        <p className="text-sm text-[color:var(--alb-text-muted)]">—</p>
                    ) : (
                        <ul className="space-y-2">
                            {blocked.map((u) => (
                                <li key={u.user_id} className="flex items-center justify-between gap-3">
                                    <span className="text-sm">{u.name}</span>
                                    <Button size="sm" variant="outline" onClick={() => unblockFriend(u.user_id)}>{t('friends.unblock_user')}</Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            )}

            {requests.length > 0 && (
                <Card className="p-4 mb-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="friends-requests-panel">
                    <h2 className="font-semibold mb-3">{t('friends.requests_title')}</h2>
                    <ul className="space-y-3">
                        {requests.map((f) => (
                            <li key={f.user.user_id} data-testid="friends-request-item" className="flex flex-col gap-3 rounded-lg border border-[color:var(--alb-hairline)] p-3 sm:flex-row sm:items-center">
                                <Link to={`/usuari/${f.user.user_id}`} className="flex items-center gap-3 min-w-0 flex-1">
                                    <Avatar className="h-10 w-10">
                                        <AvatarImage src={f.user.picture} />
                                        <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(f.user.name || 'U').slice(0, 1).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0">
                                        <div className="font-semibold truncate">
                                            {f.user.name}
                                            <span className="ml-2 rounded-full bg-[color:var(--alb-aqua)]/20 px-2 py-0.5 text-[10px] uppercase tracking-wide">{t('friends.request_badge')}</span>
                                        </div>
                                        {f.last_message?.content && <div className="text-xs text-[color:var(--alb-text-muted)] truncate">{f.last_message.content}</div>}
                                    </div>
                                </Link>
                                <div className="flex gap-2 shrink-0">
                                    <Button size="sm" onClick={() => acceptRequest(f.user.user_id)} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" data-testid="friends-request-accept">{t('friends.request_accept')}</Button>
                                    <Button size="sm" variant="outline" onClick={() => ignoreRequest(f.user.user_id)} data-testid="friends-request-ignore">{t('friends.request_ignore')}</Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            <div className="grid md:grid-cols-[280px_1fr] gap-4 rounded-[16px] border border-[color:var(--alb-hairline)] overflow-hidden bg-[color:var(--alb-surface)]" style={{ minHeight: '60vh' }}>
                <div className="border-r border-[color:var(--alb-hairline)] overflow-y-auto">
                    {chatFriends.length === 0 ? (
                        <div className="p-5 text-center">
                            <div className="font-display text-lg mb-1">{t('friends.empty_title')}</div>
                            <div className="text-sm text-[color:var(--alb-text-muted)]">{t('friends.empty_subtitle')}</div>
                        </div>
                    ) : (
                        <ul>
                            {chatFriends.map((f) => (
                                <li key={f.user.user_id}>
                                    <button
                                        type="button"
                                        onClick={() => setActive(f)}
                                        data-testid="friends-list-item"
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 ${active?.user.user_id === f.user.user_id ? 'bg-white/10' : ''}`}
                                    >
                                        <Avatar className="h-10 w-10">
                                            <AvatarImage src={f.user.picture} />
                                            <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(f.user.name || 'U').slice(0, 1).toUpperCase()}</AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-semibold truncate">{f.user.name}</span>
                                                {f.unread_count > 0 && <span className="rounded-full bg-[color:var(--alb-pink)] text-white text-[10px] px-1.5 py-0.5">{f.unread_count}</span>}
                                            </div>
                                            <div className="text-xs text-[color:var(--alb-text-muted)] truncate">
                                                {f.last_message ? (f.last_message.content || (f.last_message.transfer_amount ? `Diners: ${f.last_message.transfer_amount.toFixed(2)} EUR` : f.last_message.image_url ? t('friends.attach_image') : f.last_message.shared_video ? t('friends.shared_video_label') : '')) : ''}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="flex flex-col">
                    {!active ? (
                        <div className="flex-1 grid place-items-center text-sm text-[color:var(--alb-text-muted)]">{t('friends.select_conversation')}</div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[color:var(--alb-hairline)]">
                                <Link to={`/usuari/${active.user.user_id}`} className="flex items-center gap-3">
                                    <Avatar className="h-9 w-9">
                                        <AvatarImage src={active.user.picture} />
                                        <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(active.user.name || 'U').slice(0, 1).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <span className="font-semibold">{active.user.name}</span>
                                </Link>
                                <Button variant="outline" size="sm" onClick={() => blockFriend(active.user.user_id)} data-testid="friends-block-button"><Shield className="h-4 w-4 mr-1" /> {t('friends.block_user')}</Button>
                            </div>

                            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                                {messages.length === 0 && <div className="text-sm text-[color:var(--alb-text-muted)] text-center mt-6">{t('friends.no_messages_yet')}</div>}
                                {messages.map((m) => {
                                    const mine = m.sender_id === user.user_id;
                                    return (
                                        <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]' : 'bg-[color:var(--alb-surface-2)]'}`}>
                                                {m.content && <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>}
                                                {typeof m.transfer_amount === 'number' && (
                                                    <div className="mt-1 flex items-center gap-2 rounded-lg bg-black/10 px-3 py-2" data-testid={`chat-transfer-${m.message_id}`}>
                                                        <Banknote className="h-4 w-4" />
                                                        <span className="text-sm font-bold font-mono-tab">{m.transfer_amount.toFixed(2)} EUR</span>
                                                        <span className="text-xs opacity-80">{mine ? 'Enviat' : 'Rebut'}</span>
                                                    </div>
                                                )}
                                                {m.image_url && <img src={fileUrl(m.image_url)} alt="" className="mt-1 max-h-64 rounded-lg object-cover" />}
                                                {m.shared_video && (
                                                    !m.shared_video.is_premium && !m.shared_video.is_adult && m.shared_video.video_url ? (
                                                        <div className="mt-1" data-testid={`chat-shared-video-${m.message_id}`}>
                                                            <video controls preload="metadata" playsInline poster={m.shared_video.thumbnail_url ? fileUrl(m.shared_video.thumbnail_url) : undefined} src={fileUrl(m.shared_video.video_url)} className="max-h-72 w-full rounded-lg bg-black" />
                                                            <Link to={`/video/${m.shared_video.video_id}`} className="mt-1 flex items-center gap-1 text-xs font-semibold opacity-80 hover:opacity-100"><Film className="h-3.5 w-3.5" /> {m.shared_video.title}</Link>
                                                        </div>
                                                    ) : (
                                                        <Link to={`/video/${m.shared_video.video_id}`} className="mt-1 flex items-center gap-2 rounded-lg bg-black/10 p-2">
                                                            {m.shared_video.thumbnail_url ? (
                                                                <img src={fileUrl(m.shared_video.thumbnail_url)} alt="" className="h-12 w-20 object-cover rounded" />
                                                            ) : <Film className="h-8 w-8" />}
                                                            <span className="text-xs font-semibold truncate">{m.shared_video.title}</span>
                                                        </Link>
                                                    )
                                                )}
                                                <div className="mt-1 text-[10px] opacity-70 text-right">{timeAgoCa(m.created_at)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {showMoneyForm && (
                                <div className="space-y-2 border-t border-[color:var(--alb-hairline)] p-3" data-testid="friends-send-money-panel">
                                    <div className="text-sm font-semibold">Enviar diners a {active.user.name} · saldo {walletBalance.toFixed(2)} EUR</div>
                                    {pinChallenge === null && <p className="text-xs text-[color:var(--alb-text-muted)]">Carregant el repte de seguretat...</p>}
                                    {pinChallenge && pinChallenge.has_pin === false && (
                                        <p className="text-sm">Per seguretat, primer has de configurar el teu PIN de 6 xifres al <Link to="/perfil" className="underline">perfil</Link> (secció Cartera).</p>
                                    )}
                                    {pinChallenge && pinChallenge.has_pin === true && (
                                        <>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <Label className="text-xs">Quantitat (EUR)</Label>
                                                    <Input type="number" step="0.01" min="0.01" max={walletBalance} value={moneyAmount} onChange={(e) => setMoneyAmount(e.target.value)} className="bg-[color:var(--alb-ink-2)]" data-testid="money-amount-input" />
                                                </div>
                                                <div>
                                                    <Label className="text-xs">Contrasenya</Label>
                                                    <Input type="password" value={moneyPassword} onChange={(e) => setMoneyPassword(e.target.value)} className="bg-[color:var(--alb-ink-2)]" data-testid="money-password-input" />
                                                </div>
                                                <div className="col-span-2">
                                                    <Label className="text-xs">Repeteix la contrasenya</Label>
                                                    <Input type="password" value={moneyPassword2} onChange={(e) => setMoneyPassword2(e.target.value)} className="bg-[color:var(--alb-ink-2)]" data-testid="money-password2-input" />
                                                </div>
                                            </div>
                                            <div className="flex items-end gap-2">
                                                {(pinChallenge.positions || []).map((pos) => (
                                                    <div key={pos}>
                                                        <Label className="text-xs">Xifra {pos + 1}a del PIN</Label>
                                                        <Input type="password" autoComplete="off" maxLength={1} inputMode="numeric" value={pinDigits[String(pos)] || ''} onChange={(e) => setPinDigits({ ...pinDigits, [String(pos)]: e.target.value.replace(/\D/g, '') })} className="w-12 bg-[color:var(--alb-ink-2)] text-center" data-testid={`money-pin-digit-${pos}`} />
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex gap-2">
                                                <Button type="button" disabled={moneyBusy} onClick={confirmSendMoney} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" data-testid="money-send-confirm"><Banknote className="h-4 w-4 mr-1" /> Enviar diners</Button>
                                                <Button type="button" variant="outline" onClick={() => setShowMoneyForm(false)}>Cancel·lar</Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            <form onSubmit={sendMessage} className="flex items-center gap-2 border-t border-[color:var(--alb-hairline)] p-3">
                                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={sendImage} data-testid="friends-image-input" />
                                <Button type="button" variant="outline" size="icon" onClick={() => fileInputRef.current?.click()} title={t('friends.attach_image')}><ImageIcon className="h-4 w-4" /></Button>
                                {walletBalance > 0 && (
                                    <Button type="button" variant="outline" size="icon" disabled={moneyBusy} onClick={openMoneyForm} title={`Enviar diners (saldo: ${walletBalance.toFixed(2)} EUR)`} data-testid="friends-send-money-button"><Banknote className="h-4 w-4" /></Button>
                                )}
                                <Input
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t('friends.message_placeholder')}
                                    className="bg-[color:var(--alb-ink-2)]"
                                    data-testid="friends-message-input"
                                />
                                <Button type="submit" disabled={sending || !text.trim()} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" data-testid="friends-send-button"><Send className="h-4 w-4" /></Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
